// "Activar unidad" — the dashboard half of ADR-0124's single domain fact:
// activating a unit IS inviting its owner. One call, one drawer, two
// required fields (plus an optional phone, ADR-0130), and a link the supervisor delivers themselves.
//
// Three structural decisions that are not free to change:
//
//   1. `createPortal(..., document.body)`. The trigger lives in a <td>
//      inside a <tr>, and this drawer is `position: fixed` with a
//      backdrop. Rendering it in place would nest a <div>/<aside> inside
//      a table row — invalid HTML the parser hoists out, which in React
//      means the DOM no longer matches the tree and the drawer's own
//      focus/Escape handling lands on a detached node. The portal is the
//      fix, not a preference.
//
//   2. `useDialogFocus` from ../lib/useDialogFocus — the canonical hook,
//      not ShareLinkDrawer's local `useFocusTrap`. Two implementations of
//      the same WAI-ARIA contract in one app is how they drift; the local
//      one is on its way out (its own follow-up ticket).
//
//   3. `idemKeyRef` is minted ONCE per mount and replayed on every retry.
//      The route mints a permanent artifact (an invited account plus an
//      activated unit) behind a synchronous call. A lost response plus a
//      retry under a fresh key is two invitations to the same address
//      (#0472). The plugin caches only 2xx, so a retry after a fan-out
//      400 re-executes under the same key — which is precisely what the
//      INVITATION_FANOUT_FAILED branch below asks the user to do.

import { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { isValidOwnerPhone, type CarteraDashboardAssetItem } from "@bitacorapro/api-types";
import { ApiError } from "../lib/api/error";
import { useActivateUnit } from "../lib/api/queries";
import { focusHeading, useDialogFocus } from "../lib/useDialogFocus";
import { Icon } from "./icons";
import { CopyLinkField } from "./CopyLinkField";

// Same shape the PWA's activar page validates with — deliberately loose.
// The authoritative check is `emailSchema` server-side; this one only
// decides whether the submit button is worth enabling.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// How a failure is allowed to end. The distinction that matters is not
// the HTTP status but what the user can still DO:
//   role    — the caller's role can't invite at all. Terminal for them,
//             but the form stays enabled: an admin may be sitting next to
//             them, and disabling the fields would discard typed input
//             for a handover that takes ten seconds.
//   scope   — the unit is not theirs (or is gone). The fields go dead:
//             there is no value they could type that changes the answer.
//   bound   — 409. The unit already has an owner with an account. No
//             form at all — the only action left is Cerrar, and changing
//             an owner is not a thing the app does yet.
//   generic — recipe misconfiguration, fan-out failure, network, or
//             anything unrecognized. Values are kept and the same button
//             retries under the same idempotency key.
type ActivateErrorKind = "role" | "scope" | "bound" | "generic";

type MappedError = { kind: ActivateErrorKind; message: string };

export function mapActivateUnitError(err: unknown): MappedError {
  const code = err instanceof ApiError ? err.code : null;
  switch (code) {
    case "INVITATION_CREATE_ROLE_REQUIRED":
    case "INVITATION_ROLE_NOT_GRANTABLE":
      return {
        kind: "role",
        message:
          "Tu rol no tiene permiso para invitar propietarios. Pide a un administrador o gerente de postventa que la active.",
      };
    case "ASSET_OUT_OF_SCOPE":
      return {
        kind: "scope",
        message:
          "No pudimos abrir esta unidad. Es posible que ya no esté disponible o que no tengas acceso a ella.",
      };
    case "OWNER_ALREADY_BOUND":
      return {
        kind: "bound",
        message:
          "No podemos invitar a otra persona mientras la unidad siga vinculada a este propietario. Cambiar de propietario es un proceso que todavía no está disponible en la app — si lo necesitas, contacta a soporte.",
      };
    default:
      // RECIPE_HAS_NO_OWNER_ROLE / RECIPE_OWNER_ROLE_AMBIGUOUS /
      // RECIPE_HAS_NO_ACTIVATION / RECIPE_OWNER_ROLE_NOT_STAMPABLE /
      // INVITATION_FANOUT_FAILED / NETWORK / anything new the server
      // grows. All of them are "try again, and if it keeps happening
      // it's ours, not yours" from where the supervisor sits.
      return {
        kind: "generic",
        message:
          "No pudimos enviar la invitación. Puede ser un problema de conexión — vuelve a intentar.",
      };
  }
}

export function ActivateUnitDrawer({
  asset,
  assetLabel,
  alreadyActivated,
  onClose,
}: {
  asset: CarteraDashboardAssetItem;
  /** The recipe's word for one unit ("Unidad"), for the drawer's lead. */
  assetLabel: string;
  /** True when the row already carries the activation status — the
   *  drawer then reads as a re-invite rather than a first activation. */
  alreadyActivated: boolean;
  onClose: () => void;
}) {
  const titleId = useId();
  const nameId = useId();
  const emailId = useId();
  const nameErrorId = `${nameId}-error`;
  const emailErrorId = `${emailId}-error`;
  const phoneId = useId();
  const phoneHintId = `${phoneId}-hint`;
  const phoneErrorId = `${phoneId}-error`;

  const drawerRef = useRef<HTMLElement>(null);
  const successRef = useRef<HTMLHeadingElement>(null);
  const idemKeyRef = useRef<string>(crypto.randomUUID());

  const [ownerName, setOwnerName] = useState("");
  const [ownerEmail, setOwnerEmail] = useState("");
  const [nameTouched, setNameTouched] = useState(false);
  const [emailTouched, setEmailTouched] = useState(false);
  // Opcional (ADR-0130). No se pre-llena: el drawer tampoco pre-llena
  // nombre ni email, y la fila de la tabla no trae los atributos de
  // contacto. Vacío = el body no lo lleva y el servidor no toca el
  // teléfono que la unidad ya tenga (p. ej. el de la planilla).
  const [ownerPhone, setOwnerPhone] = useState("");
  const [phoneTouched, setPhoneTouched] = useState(false);
  const [submitError, setSubmitError] = useState<MappedError | null>(null);

  const mutation = useActivateUnit();

  useDialogFocus(true, drawerRef);

  // The success body replaces the form inside an already-open dialog —
  // a content swap, not a re-open — so focus moves to the new heading
  // rather than re-running useDialogFocus's open sequence. Same posture
  // as ChecklistTemplateAssignDrawer's panel A ↔ B swap.
  useEffect(() => {
    if (mutation.isSuccess) focusHeading(successRef);
  }, [mutation.isSuccess]);

  const nameValid = ownerName.trim().length > 0;
  const emailValid = EMAIL_RE.test(ownerEmail.trim());
  // Misma regla que el body del servidor (api-types `isValidOwnerPhone`),
  // así lo que el formulario deja pasar es lo que el portal de
  // contratistas va a mostrar con su «Llamar».
  const phoneTrimmed = ownerPhone.trim();
  const phoneValid = phoneTrimmed === "" || isValidOwnerPhone(phoneTrimmed);
  const canSubmit = nameValid && emailValid && phoneValid;

  const showNameError = nameTouched && !nameValid;
  const showEmailError = emailTouched && !emailValid;
  const showPhoneError = phoneTouched && !phoneValid;

  // A scope failure is the one branch where re-typing cannot help, so the
  // fields go dead. Everything else keeps them live for a retry.
  const fieldsDisabled = mutation.isPending || submitError?.kind === "scope";
  const formHidden = mutation.isSuccess || submitError?.kind === "bound";

  async function onSubmit() {
    if (!canSubmit || mutation.isPending) return;
    setSubmitError(null);
    try {
      await mutation.mutateAsync({
        assetId: asset.id,
        body: {
          ownerName: ownerName.trim(),
          ownerEmail: ownerEmail.trim(),
          ...(phoneTrimmed !== "" ? { ownerPhone: phoneTrimmed } : {}),
        },
        idempotencyKey: idemKeyRef.current,
      });
    } catch (err) {
      setSubmitError(mapActivateUnitError(err));
    }
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Escape") onClose();
  }

  const heading = alreadyActivated ? "Reenviar invitación" : "Activar unidad";
  const intro = alreadyActivated
    ? "Esta unidad ya está activada. Vas a generar un enlace nuevo para el propietario; el enlace anterior sigue funcionando hasta que venza."
    : "Vas a activar esta unidad e invitar a su propietario. Desde que acepte la invitación, podrá enviar observaciones sobre la propiedad.";

  return createPortal(
    <>
      <div className="drawer-backdrop" onClick={onClose} role="presentation" />
      <aside
        ref={drawerRef}
        className="drawer"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        onKeyDown={onKeyDown}
      >
        <div className="drawer__head">
          <h2 id={titleId} className="drawer__title">
            {heading}
          </h2>
          <button type="button" className="drawer__close" onClick={onClose} aria-label="Cerrar">
            <Icon.Close />
          </button>
        </div>

        <div className="drawer__body">
          {mutation.isSuccess && mutation.data ? (
            <>
              <h3 ref={successRef} tabIndex={-1} className="drawer__obs-title">
                Invitación creada
              </h3>
              {/* El correo SÍ sale: self-service-invitation.ts fanea a
                  /auth/invitations de auth-svc, que llama a
                  sendInvitationEmail. Pero el envío es best-effort (los
                  fallos se loguean y nunca suben a 500), así que el
                  enlace copiable es el respaldo real, no el canal único
                  — que es lo que decía este copy antes. */}
              {/* Copy neutro (revisión de la fase 1, p2): la respuesta ya
                  no dice si el correo tiene cuenta — se calculaba sobre
                  todas las organizaciones y revelaba si la persona era
                  usuaria de otra inmobiliaria. Por eso el enlace se
                  muestra siempre, sin prometer que funcione: si el
                  correo es de otra org o del personal, el servidor no
                  envía y aceptar responde 409. */}
              <p className="cell-sub drawer__obs-meta">
                Si el correo es correcto, le llegará la invitación. Si ya tiene una cuenta en
                BitácoraPro, podrá iniciar sesión con ella al abrirla. También puedes enviarle este
                enlace por WhatsApp o como prefieras.
              </p>
              <CopyLinkField
                id="activar-accept-url"
                label="Enlace de invitación"
                value={mutation.data.acceptUrl}
              />
              <p className="cell-sub drawer__note">
                Guárdalo ahora: si vuelves a esta pantalla más adelante para reenviar, se genera un
                enlace distinto, no este mismo.
              </p>
            </>
          ) : submitError?.kind === "bound" ? (
            <>
              <h3 className="drawer__obs-title">Esta unidad ya tiene un propietario con cuenta</h3>
              <p className="cell-sub drawer__obs-meta">{submitError.message}</p>
            </>
          ) : (
            <>
              <p className="cell-sub drawer__obs-meta">
                {assetLabel}: {asset.address || asset.unit || "(sin dirección)"}
              </p>
              <p className="cell-sub">{intro}</p>

              {submitError && (
                <p className="auth-error" role="alert">
                  {submitError.message}
                </p>
              )}

              <div className="field">
                <label className="field__label" htmlFor={nameId}>
                  Nombre del propietario
                </label>
                <input
                  id={nameId}
                  className="input"
                  type="text"
                  autoComplete="name"
                  maxLength={120}
                  required
                  value={ownerName}
                  disabled={fieldsDisabled}
                  onChange={(e) => setOwnerName(e.target.value)}
                  onBlur={() => setNameTouched(true)}
                  aria-invalid={showNameError || undefined}
                  aria-describedby={showNameError ? nameErrorId : undefined}
                />
                {showNameError && (
                  <p id={nameErrorId} className="field__hint" role="alert">
                    Necesitamos el nombre del propietario para generar la invitación.
                  </p>
                )}
              </div>

              <div className="field">
                <label className="field__label" htmlFor={emailId}>
                  Email
                </label>
                <input
                  id={emailId}
                  className="input"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  placeholder="correo@ejemplo.cl"
                  required
                  value={ownerEmail}
                  disabled={fieldsDisabled}
                  onChange={(e) => setOwnerEmail(e.target.value)}
                  onBlur={() => setEmailTouched(true)}
                  aria-invalid={showEmailError || undefined}
                  aria-describedby={showEmailError ? emailErrorId : undefined}
                />
                {showEmailError && (
                  <p id={emailErrorId} className="field__hint" role="alert">
                    Ingresa un email válido.
                  </p>
                )}
              </div>

              <div className="field">
                <label className="field__label" htmlFor={phoneId}>
                  Teléfono
                </label>
                <input
                  id={phoneId}
                  className="input"
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  placeholder="+56 9 1234 5678"
                  maxLength={40}
                  value={ownerPhone}
                  disabled={fieldsDisabled}
                  onChange={(e) => setOwnerPhone(e.target.value)}
                  onBlur={() => setPhoneTouched(true)}
                  aria-invalid={showPhoneError || undefined}
                  aria-describedby={showPhoneError ? phoneErrorId : phoneHintId}
                />
                {showPhoneError ? (
                  <p id={phoneErrorId} className="field__hint" role="alert">
                    Ingresa un teléfono válido, por ejemplo +56 9 1234 5678 o 912345678.
                  </p>
                ) : (
                  <p id={phoneHintId} className="field__hint">
                    Opcional. El contratista lo usa para coordinar la visita.
                  </p>
                )}
              </div>
            </>
          )}
        </div>

        <div className="drawer__foot">
          <button type="button" className="btn btn--ghost" onClick={onClose}>
            {formHidden || submitError?.kind === "scope" ? "Cerrar" : "Cancelar"}
          </button>
          {!formHidden && submitError?.kind !== "scope" && (
            <button
              type="button"
              className="btn btn--primary"
              onClick={() => void onSubmit()}
              disabled={!canSubmit || mutation.isPending}
            >
              {mutation.isPending ? "Enviando invitación…" : "Enviar invitación"}
            </button>
          )}
        </div>
      </aside>
    </>,
    document.body
  );
}
